import { InputTextCustom, EInputTypes, Combobox, Modal, Notification, useFormValidation, setNotification, ETypeOfNotification } from 'medere-ui-framework'
import { InputTextarea } from 'primereact/inputtextarea'
import { TabView, TabPanel } from 'primereact/tabview'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Accounting, AccountingAccount, DocumentType, EarningWithholdingConceptDTO, EVatCustomerCategory, PaymentCondition, PaymentMethod, SupplierTypeDTO, VatCustomerCategory } from '../../interface/interface'
import { getSupplierType, getPaymentMethods, getPaymentConditions, getVatCustomerCategories, getEarningWithHoldings, saveSupplier, updateSupplier } from '../../services/SupplierController'

import { Cuit, handleCUITValidation } from '../../utils/utils'
import { AxiosResponse } from 'axios'
import { useDispatch } from "react-redux"
import { ComboDataDTO } from '../../dto/ComboDataDTO'
import CityLocationCombo from '../../components/CityLocationCombo'
import { useWithholdingService } from '../../servicesHooks/Withholding/useWithholdingService'
import { useSupplierService } from '../../servicesHooks/Supplier/useSupplierService'
import { useEntityService } from '../../servicesHooks/Entity/useEntityService'
import { useLocationService } from '../../servicesHooks/Location/useLocationService'
import { loadDocumentTypes } from '../../servicesHooks/ProfileDataForm/useProfileDataFormService'

export interface SupplierForm {
    medereentity?: number
    businessName: string
    commercialName: string
    phone: string
    secondaryPhone: string
    email: string
    postalCode: string
    observations: string
    city: ComboDataDTO | null
    active: boolean
    daysForExpiration: number
    exempt: boolean
    exemptDate: Date | null
    condIva: VatCustomerCategory | null
    documentType: DocumentType | null
    fiscalDocument: string
    invoiceAddress: string
    supplierType: SupplierTypeDTO | null
    accounting: Accounting | null
    credit: AccountingAccount | null
    debit: AccountingAccount | null
    subAccounting: AccountingAccount | null
    paymentMethod: PaymentMethod | null
    paymentCondition: PaymentCondition | null
    earningWithHoldingConcept: EarningWithholdingConceptDTO | null
}

export interface SupplierModalProps {
    isOpen: boolean,
    setIsOpen: (e: boolean) => void
    refreshTable?: () => void
    supplier?: SupplierForm | null
    setSupplier?: React.Dispatch<React.SetStateAction<SupplierForm | null>>,
}

function SupplierModal({ isOpen, setIsOpen, refreshTable, supplier, setSupplier }: SupplierModalProps) {
    const toast = useRef(null)
    const dispatch = useDispatch();
    const { getEarningWithholdingConcepts, getEarningWithholdingConceptById } = useWithholdingService();
    const { getSupplierType, getPaymentMethods, getPaymentConditions, getVatCustomerCategories, getEarningWithHoldings, saveSupplier, updateSupplier } = useSupplierService();
    const { getDefaultCity } = useEntityService();
    const { getCitiesFromMedereData } = useLocationService();
    const [generalForm, setGeneralForm] = useState<SupplierForm>({
        medereentity: 0,
        businessName: '',
        commercialName: '',
        phone: '',
        secondaryPhone: '',
        email: '',
        postalCode: '',
        city: null,
        active: true,
        exempt: false,
        daysForExpiration: 0,
        exemptDate: null,
        condIva: null,
        documentType: null,
        fiscalDocument: '',
        invoiceAddress: '',
        supplierType: null,
        accounting: null,
        credit: null,
        debit: null,
        subAccounting: null,
        paymentMethod: null,
        paymentCondition: null,
        observations: '',
        earningWithHoldingConcept: null,
    })

    const { handleValidation, errors, cleanError, setErrors } = useFormValidation(generalForm)
    const [loading, setLoading] = useState(false)
    const [edited, setEdited] = useState(false)
    const ERROR_MESSAGE = 'Este campo es necesario para continuar'

    useEffect(() => {
        if (supplier != null) {
            setGeneralForm(supplier)
        }
    }, [supplier])

    useEffect(() => {
        if (generalForm?.earningWithHoldingConcept && !edited) {
            setEdited(true)
            getEarningWithholdingConceptById(generalForm.earningWithHoldingConcept.withholdingconcept)
                .then((item: EarningWithholdingConceptDTO) => {
                    if (item) {
                        setGeneralForm(prev => ({ ...prev, earningWithHoldingConcept: item }));
                    }
                }).catch((error) => {
                    console.error('Error al cargar earningWithHoldingConcept:', error);
                });
        }
    }, [generalForm?.earningWithHoldingConcept]);

    useEffect(() => {
        getDefaultCity().then((resp) => {
            setGeneralForm(prev => prev.city ? prev : ({
                ...prev,
                city: resp
            }))
        })

        loadDocumentTypes({
            filterValue: "",
            offSet: 0,
            pageSize: 0
        }).then((data) => {
            if (data && data.length > 0) {
                const defaultDocumentType = data.find(item => item.shortName === 'CUIT');
                setGeneralForm((prev) => prev.documentType ? prev : ({ ...prev, documentType: defaultDocumentType }));
            }
        });
    }, [])

    function hasErrors() {
        const _validation = handleValidation(() => { }, errors, [
            'commercialName', 'phone', 'secondaryPhone', 'email', 'postalCode', 'observations',
            'city', 'documentType', 'fiscalDocument', 'invoiceAddress', 'supplierType',
            'paymentMethod', 'paymentCondition', 'condIva', 'exemptDate', 'exempt',
            'medereentity', 'daysForExpiration', 'active', 'accounting', 'credit', 'debit',
            'subAccounting', 'earningWithHoldingConcept'
        ])
        const hasName = generalForm.businessName.trim() !== '' || generalForm.commercialName.trim() !== ''
        if (!hasName) {
            setErrors(prev => ({ ...prev, businessName: true, commercialName: true }))
        }
        return _validation.invalidFields.length !== 0 || !hasName
    }

    async function resolveCityForSave(form: SupplierForm) {
        const selectedCity = form.city

        if (!selectedCity) {
            return form
        }

        if (selectedCity.id != null && selectedCity.id > 0) {
            return form
        }

        const query = selectedCity.name?.trim()
        if (!query) {
            return form
        }

        try {
            const matches = await getCitiesFromMedereData({ filterValue: query, offSet: 0, pageSize: 50 })
            const normalizedQuery = query.toLowerCase()
            const exactMatch = matches.find((item: ComboDataDTO) => item?.name?.trim().toLowerCase() === normalizedQuery)
            const resolvedCity = exactMatch ?? matches[0] ?? null

            if (resolvedCity?.id != null) {
                return {
                    ...form,
                    city: resolvedCity
                }
            }
        } catch (error) {
            console.error('Error resolving city before save:', error)
        }

        return form
    }

    async function handleModal(functionSupplier: (generalForm: SupplierForm) => Promise<AxiosResponse<any, any>>) {
        setLoading(true);

        if (hasErrors()) {
            setLoading(false);
            return;
        }

        const formToSave = await resolveCityForSave(generalForm)

        functionSupplier(formToSave)
            .then(() => {
                if (refreshTable) refreshTable();
                cleanVariables();
                setIsOpen(false);
                dispatch(setNotification({
                    title: supplier ? 'Editar proveedor' : 'Agregar proveedor',
                    desc: 'El proveedor se ha ' + (supplier ? 'modificado' : 'creado') + ' con éxito',
                    typeOfNotification: ETypeOfNotification.INFO,
                    icon: 'pi pi-check'
                }));
            })
            .catch(err => {
                const message = err?.response?.data?.toString() ?? '';

                if (message.includes('CUIT ingresado no es válido')) {
                    dispatch(setNotification({
                        title: 'Error en CUIT',
                        desc: 'El CUIT ingresado no existe o no es válido.',
                        typeOfNotification: ETypeOfNotification.ERROR,
                        icon: 'pi pi-exclamation-triangle'
                    }));
                } else if (message.includes('duplicate key')) {
                    dispatch(setNotification({
                        title: 'CUIT duplicado',
                        desc: 'Ya existe un proveedor con ese CUIT.',
                        typeOfNotification: ETypeOfNotification.ERROR,
                        icon: 'pi pi-exclamation-triangle'
                    }));
                } else {
                    dispatch(setNotification({
                        title: 'Error inesperado',
                        desc: 'No se pudo guardar el comprobante.',
                        typeOfNotification: ETypeOfNotification.ERROR,
                        icon: 'pi pi-times'
                    }));
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function cleanVariables() {
        setIsOpen(false)
        setGeneralForm({
            medereentity: 0,
            businessName: '',
            commercialName: '',
            phone: '',
            secondaryPhone: '',
            email: '',
            postalCode: '',
            observations: '',
            city: null,
            active: true,
            exempt: false,
            daysForExpiration: 0,
            exemptDate: null,
            condIva: null,
            documentType: null,
            fiscalDocument: '',
            invoiceAddress: '',
            supplierType: null,
            accounting: null,
            credit: null,
            debit: null,
            subAccounting: null,
            paymentMethod: null,
            paymentCondition: null,
            earningWithHoldingConcept: null,
        })
        if (setSupplier) setSupplier(null)
    }

    function supplierModalContent() {
        return <TabView>
            <TabPanel header="General" leftIcon='pi pi-building mr-2'>
                <div className='md:flex md:justify-content-between md:align-items-center md:gap-6'>
                    <InputTextCustom
                        label='Nombre comercial'
                        value={generalForm.commercialName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setGeneralForm(prev => ({
                                ...prev,
                                commercialName: e.target.value
                            }))
                            if (e.target.value.trim() != '') {
                                cleanError('commercialName')
                            }
                        }}
                        inputType={EInputTypes.TEXT}
                        error={errors.commercialName}
                        caption={errors.fiscalDocument && !generalForm.fiscalDocument ? ERROR_MESSAGE : errors.fiscalDocument ? 'El cuit es invalido' : ''}
                    />
                    <InputTextCustom
                        label='Razon social'

                        value={generalForm.businessName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setGeneralForm(prev => ({
                                ...prev,
                                businessName: e.target.value
                            }))
                            if (e.target.value.trim() != '') {
                                cleanError('businessName')
                            }
                        }}
                        error={errors.businessName}
                        caption={errors.businessName ? ERROR_MESSAGE : ''}
                    />
                </div>
                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <InputTextCustom
                        label='Teléfono'
                        value={generalForm.phone}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setGeneralForm(prev => ({
                            ...prev,
                            phone: e.target.value
                        }))}
                        error={errors.phone}
                        caption={errors.phone ? ERROR_MESSAGE : ''}
                    />
                    <InputTextCustom
                        label='Teléfono secundario'
                        value={generalForm.secondaryPhone}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setGeneralForm(prev => ({
                            ...prev,
                            secondaryPhone: e.target.value
                        }))}
                        error={errors.secondaryPhone}
                        caption={errors.secondaryPhone ? ERROR_MESSAGE : ''}
                    />
                </div>
                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <InputTextCustom
                        label='Email'
                        name="email"
                        value={generalForm.email}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setGeneralForm(prev => ({
                                ...prev,
                                email: e.target.value
                            }))
                            if (e.target.value.trim() != '') {
                                cleanError('email')
                            }
                        }}
                        error={errors.email}
                        caption={errors.email ? ERROR_MESSAGE : ''}
                    />
                    <InputTextCustom
                        label='Domicilio fiscal'
                        value={generalForm.invoiceAddress}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setGeneralForm(prev => ({
                                ...prev,
                                invoiceAddress: e.target.value
                            }))
                            if (e.target.value.trim() != '') {
                                cleanError('invoiceAddress')
                            }
                        }}
                        error={errors.invoiceAddress}
                        caption={errors.invoiceAddress ? ERROR_MESSAGE : ''}
                    />
                </div>
                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <CityLocationCombo
                        label='Ciudad'
                        value={generalForm.city}
                        onChange={(e: ComboDataDTO | null) => {
                            setGeneralForm(prev => ({
                                ...prev,
                                city: e
                            }))
                            if (e !== null) {
                                cleanError('city')
                            }
                        }}
                        error={errors.city}
                    />
                    <InputTextCustom
                        label='Cód. Postal'
                        value={generalForm.postalCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setGeneralForm(prev => ({
                            ...prev,
                            postalCode: e.target.value
                        }))}
                    />
                </div>

                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <Combobox
                        label='Tipo'
                        getItems={({ filterValue, offSet, pageSize }) => loadDocumentTypes({ filterValue, offSet, pageSize }).then((data => {
                            return data.filter((item: DocumentType) => item.shortName === 'CUIT' || item.shortName === 'CUIL')
                        }))}
                        optionLabel='shortName'
                        value={generalForm.documentType}
                        setValue={(e) => {
                            setGeneralForm(prev => ({ ...prev, documentType: e }))
                            if (e !== null) {
                                cleanError('documentType')
                            }
                        }}
                        error={errors.documentType}
                        caption={errors.documentType ? ERROR_MESSAGE : ''}
                    />
                    <InputTextCustom
                        label='Nº (con guion)'
                        value={generalForm.fiscalDocument}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            if (e.target.value.trim() !== '' && !handleCUITValidation(e.target.value as Cuit)) {
                                setErrors(prev => ({ ...prev, fiscalDocument: true }))
                            } else {
                                cleanError('fiscalDocument')
                            }
                            setGeneralForm(prev => ({
                                ...prev,
                                fiscalDocument: e.target.value
                            }))
                        }}
                        placeholder='Ej: 20-12345678-9'
                        error={errors.fiscalDocument}
                        caption={errors.fiscalDocument && !generalForm.fiscalDocument ? ERROR_MESSAGE : errors.fiscalDocument ? 'El cuit es invalido' : ''}
                    />
                </div>
                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <Combobox label='Rubro'
                        getItems={getSupplierType}
                        optionLabel='fulldescription'
                        value={generalForm.supplierType}
                        setValue={(e) => setGeneralForm(prev => ({ ...prev, supplierType: e }))}
                    />
                </div>
                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <Combobox
                        label='Forma de pago'
                        getItems={getPaymentMethods}
                        optionLabel='methodName'
                        value={generalForm.paymentMethod}
                        setValue={(e) => setGeneralForm(prev => ({ ...prev, paymentMethod: e }))}
                    />
                    <Combobox
                        label='Condición de pago'
                        getItems={getPaymentConditions}
                        optionLabel='description'
                        value={generalForm.paymentCondition}
                        setValue={(e) => setGeneralForm(prev => ({ ...prev, paymentCondition: e }))}
                    />
                    <InputTextCustom
                        label='Días para vencimiento'
                        value={generalForm.daysForExpiration}
                        inputType={EInputTypes.NUMBER}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setGeneralForm(prev => ({ ...prev, daysForExpiration: Number(e.target.value) }))}
                    />
                </div>
                <InputTextarea
                    autoResize
                    value={generalForm.observations ?? ''}
                    placeholder='Observaciones'
                    onChange={(e) => setGeneralForm(prev => ({ ...prev, observations: e.target.value }))}
                    rows={5}
                    cols={30}
                    className='w-full' />
            </TabPanel>

            <TabPanel header="Impuestos" leftIcon='pi pi-money-bill mr-2'>

                <div className='md:flex md:justify-content-between md:gap-6 md:align-items-center'>
                    <Combobox
                        label='Cond.Iva'
                        getItems={getVatCustomerCategories}
                        optionLabel='categoryname'
                        value={generalForm.condIva}
                        setValue={(e) => setGeneralForm(prev => ({ ...prev, condIva: e }))}
                    />
                </div>
                {generalForm.condIva?.vatcustomercategory != EVatCustomerCategory.MONOTRIBUTISTA &&
                    <Combobox label='Cód. de régimen ( RG 830 ) ' getItems={getEarningWithholdingConcepts}
                        optionLabel='description' value={generalForm.earningWithHoldingConcept} setValue={(e) => setGeneralForm(prev => ({ ...prev, earningWithHoldingConcept: e }))}
                    />
                }
                {/* <div className='md:flex md:align-items-center gap-2'>
                    <Checkbox
                        checked={generalForm.active}
                        onChange={(e) => setGeneralForm(prev => ({
                            ...prev,
                            active: e.checked ?? false
                        }))} inputId='active' />
                    <label htmlFor="active" className="label">Activo</label>
                </div> */}
                {/* <Button label="Agregar alícuota" onClick={() => {
                    handleChangeModal("IIBB", true)
                }} className='buttonMain3 justify-self-end' type='button' icon='pi pi-plus' /> */}
                {/* <CustomTable idTable='alícuota' filterRow hideFiltersButton getItems={getAllSuppliers} itemSize={30} showGridlines className='crudTable' ref={gridRef}>
                    <Column field='businessname' className='column' header='Nombre Provisional' />
                    <Column field='commercialname' className='column' header='Nombre Provisional' />
                </CustomTable> */}
            </TabPanel>

        </TabView>
    }


    return (
        <Modal
            visible={isOpen}
            setVisible={setIsOpen}
            header={supplier ? 'Editar proveedor' : 'Agregar proveedor'}
            footerButtonRightText={loading ? "Cargando..." : "Aceptar"}
            onClickRightBtn={() => {
                if (hasErrors()) {
                    dispatch(setNotification({
                        title: supplier ? 'Error al editar proveedor' : 'Error al agregar proveedor',
                        desc: 'Complete todos los campos obligatorios antes de continuar',
                        typeOfNotification: ETypeOfNotification.ERROR,
                        icon: 'pi pi-exclamation-triangle'
                    }));
                    return;
                }

                handleModal(supplier ? updateSupplier : saveSupplier);
            }}
            onHideCustom={cleanVariables}>
            {supplierModalContent()}
        </Modal>
    )
}

export default SupplierModal
